import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useCradSize } from "@/hooks/use-card-size";
import { CreateActorButton } from "@/components/custom-digital-human";
import { Card } from "./actor-card";
import type { GridProps, SkeletonProps } from "./actor-types";

// 骨架屏容器组件
function Skeleton({ columns, cardWidth }: SkeletonProps) {
  return (
    <div className="flex-1 overflow-hidden">
      <div
        className="grid p-0.5 gap-x-2 gap-y-4"
        style={{ gridTemplateColumns: `repeat(${columns}, ${cardWidth}px)` }}
      >
        {Array.from({ length: 40 }).map((_, index) => (
          <div key={index} className="w-full rounded-sm animate-pulse">
            <div className="w-full aspect-[9/16] bg-gray-300 rounded-sm mb-4"></div>
            <div className="h-3 bg-gray-300 rounded w-3/4"></div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Grid = forwardRef<HTMLDivElement, GridProps>((props, ref) => {
  const {
    loading,
    list,
    loadMore,
    toogleSelect,
    toggleFavorite,
    onRename,
    onDownload,
    onDelete,
    onRegenerate,
  } = props;
  // 无限加载逻辑
  const [visibleList, setVisibleList] = useState<TalkingActor[]>([]);
  const [paddingTop, setPaddingTop] = useState(0);
  const [paddingBottom, setPaddingBottom] = useState(0);
  const [cardWidth, setCardWidth] = useState(130);
  const [cardHeight, setCardHeight] = useState(280);
  const {
    ref: gridContainerRef,
    columns,
    width,
    height,
  } = useCradSize({
    min: 80,
    max: 160,
    gap: 8,
    padding: 10,
    resizeObserverListen: false,
  });

  // 使用 useImperativeHandle 将内部的 gridContainerRef 暴露给父组件
  useImperativeHandle(ref, () => gridContainerRef.current as HTMLDivElement);

  useEffect(() => {
    setCardWidth(width);
    setCardHeight(height);
  }, [width, height]);

  useEffect(() => {
    updateVisibleList();
    const container = gridContainerRef.current;
    if (container) {
      container.addEventListener("scroll", updateVisibleList);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", updateVisibleList);
      }
    };
  }, [list, cardWidth, cardHeight]);

  const updateVisibleList = () => {
    if (!gridContainerRef.current) {
      return;
    }

    const container = gridContainerRef.current;
    const { scrollTop, clientHeight, scrollHeight } = container;

    // 无限加载逻辑
    if (scrollTop > 0 && scrollTop + clientHeight >= scrollHeight - 400) {
      loadMore();
    }

    // 虚拟列表逻辑
    const items = list;
    const columns = Math.floor(container.clientWidth / cardWidth);
    const totalRows = Math.ceil(items.length / columns);
    const totalHeight = totalRows * cardHeight;
    const startIndex = Math.max(
      0,
      Math.floor(scrollTop / cardHeight) * columns - columns
    );
    const endIndex = Math.min(
      startIndex + Math.ceil(clientHeight / cardHeight) * columns + columns,
      items.length
    );
    setVisibleList(items.slice(startIndex, endIndex));
    setPaddingTop(Math.floor(startIndex / columns) * cardHeight);
    setPaddingBottom(
      totalHeight - Math.ceil(endIndex / columns) * cardHeight + 80
    );
  };

  if (loading && list.length === 0) {
    return <Skeleton columns={columns} cardWidth={cardWidth} />;
  }
  return (
    <div ref={gridContainerRef} className="flex-1 overflow-y-auto mr-1.5">
      <div
        style={{
          paddingTop: `${paddingTop}px`,
          paddingBottom: `${paddingBottom}px`,
        }}
      >
        <div
          className="grid p-0.5 gap-x-2 gap-y-4"
          style={{
            gridTemplateColumns: `repeat(${columns}, ${cardWidth}px)`,
          }}
        >
          <CreateActorButton onCreate={() => {}} />
          {/* 演员卡片 */}
          {visibleList.map((actor, index) => {
            return (
              <Card
                key={`${actor.id}_${index}`}
                name={actor.name}
                image={actor.image}
                video={actor.video}
                hdVideo={actor.hdVideo}
                isPro={actor.isPro}
                isHD={actor.isHD}
                isMyActor={actor.isMyActor}
                isSelected={actor.isSelected}
                isLike={actor.isFavorite}
                status={actor.status}
                isUnusual={actor.isUnusual}
                generateType={actor.generateType}
                onSelect={() => toogleSelect(actor)}
                onLike={() => toggleFavorite(actor)}
                onRename={(name) => onRename(actor, name)}
                onDownload={() => onDownload(actor)}
                onDelete={() => onDelete(actor)}
                onRegenerate={() => onRegenerate(actor)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
});

export { Grid };
